<template>
    <v-card>
        <v-tabs :value="tab" class="mt-4" show-arrows @change="$emit('switch-tab', $event)">
            <v-tab href="#tab-videos">Videos</v-tab>
            <v-tab href="#tab-results">Results</v-tab>
            <v-tab href="#tab-qualification">Qualification</v-tab>
            <v-tab href="#tab-mobility">Mobility</v-tab>
            <v-tab href="#tab-goals">Goals</v-tab>
            <v-tab href="#tab-stats">Stats</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
            <v-tab-item value="tab-videos">
                <v-card v-for="video in videos" :key="video.id">
                    <v-card-title>
                        {{ video.result.meet.title }} - {{ video.routine?.round_display }} - {{ video.event_display }}:
                        {{ video.routine?.formatted('total') }}
                    </v-card-title>
                    <routine-video :video="video" :autoplay="false" />
                </v-card>

                <v-card v-if="videos.length === 0">
                    <v-card-text> No videos found. </v-card-text>
                </v-card>
            </v-tab-item>

            <v-tab-item value="tab-results">
                <v-container class="mb-0 pb-0">
                    <v-row>
                        <v-col>
                            <v-select
                                v-model="filterEvent"
                                clearable
                                :items="events"
                                label="Filter by Event"
                                outlined
                            />
                        </v-col>
                    </v-row>
                </v-container>

                <v-data-table
                    v-if="$vuetify.breakpoint.smAndUp"
                    :headers="headers"
                    :items="athlete.results"
                    item-key="id"
                    class="elevation-1 mt-4"
                    :items-per-page="50"
                    hide-default-footer
                >
                    <template #[`item.meet.year`]="{ item: result }">
                        <datetime-display :datetime="result.meet?.start_date" format="YYYY" />
                    </template>

                    <template #[`item.meet.title`]="{ item: result }">
                        <inertia-link v-if="result.meet" :href="result.route">
                            {{ result.meet.title }}
                        </inertia-link>
                    </template>

                    <template #[`item.event`]="{ item: result }">
                        <v-chip v-if="result.event === 'TI'" color="blue" dark><strong>TRA</strong></v-chip>
                        <v-chip v-else-if="result.event === 'DM'" color="green" dark><strong>DMT</strong></v-chip>
                        <v-chip v-else-if="result.event === 'TU'" color="red" dark><strong>TUM</strong></v-chip>
                        <v-chip v-else color="yellow" dark><strong>SYN</strong></v-chip>
                    </template>

                    <template #[`item.level`]="{ item: result }">
                        <strong>{{ result.level }}</strong>
                    </template>

                    <template #[`item.overall_placement`]="{ item: result }">
                        <strong v-if="result.has_ordinal_placement">{{ ordinal(result.overall_placement) }}</strong>
                        <strong v-else>{{ result.overall_placement }}</strong>
                        <span
                            class="grey--text font-weight-bold"
                            :class="$vuetify.theme.dark ? 'text--darken-1' : 'text--lighten-1'"
                        >
                            / {{ result.category?.athlete_count }}
                        </span>
                    </template>

                    <template #[`item.total_score`]="{ item: result }">
                        <strong>
                            {{ result.eventualScore() }}
                        </strong>
                    </template>

                    <template #[`item.club.name`]="{ item: result }">
                        <inertia-link :href="`/clubs/${result.club.id}`">{{ result.club.name }}</inertia-link>
                    </template>

                    <template #[`item.view`]="{ item: result }">
                        <scorsync-video
                            v-for="routine in result.routine_results"
                            :key="routine.id"
                            :athlete="athlete"
                            :result-category="result.category"
                            :round="routine?.round"
                            :result="result"
                            :routine="routine"
                        />
                    </template>
                </v-data-table>

                <template v-else>
                    <v-card v-for="result in athlete.results" :key="result.id" class="elevation-2 my-3 mx-3 rounded-lg">
                        <div class="py-1 px-1">
                            <div class="d-flex justify-space-between">
                                <div style="min-width: 50px">
                                    <inertia-link :href="result.route" class="text-center">
                                        <div v-if="result.us_citizen" class="font-weight-bold my-0">
                                            {{ result.ordinalRank(`${result.latest_round}_placement`) }}
                                        </div>
                                        <div v-else class="font-weight-bold my-0 text-center">
                                            <v-tooltip>
                                                <template #activator="{ on, attrs }">
                                                    <span v-bind="attrs" v-on="on">
                                                        {{ result.ordinalRank(`${result.latest_round}_placement`) }}
                                                    </span>
                                                </template>
                                                Foreign athletes are ranked, but not included in the official results.
                                            </v-tooltip>
                                        </div>
                                    </inertia-link>
                                </div>
                                <div class="mb-0 mx-2" style="max-width: 55vw">
                                    <div class="text-truncated">
                                        <inertia-link :href="result.route">
                                            {{ result.meet.title }}
                                        </inertia-link>
                                    </div>
                                    <div class="grey--text caption mt-0 ml-2">
                                        <v-chip v-if="result.event === 'TI'" color="blue" dark x-small>
                                            <strong>TRA</strong>
                                        </v-chip>
                                        <v-chip v-else-if="result.event === 'DM'" color="green" dark x-small>
                                            <strong>DMT</strong>
                                        </v-chip>
                                        <v-chip v-else-if="result.event === 'TU'" color="red" dark x-small>
                                            <strong>TUM</strong>
                                        </v-chip>
                                        <v-chip v-else color="yellow" dark x-small>
                                            <strong>SYN</strong>
                                        </v-chip>

                                        {{ result.level_title }}
                                    </div>
                                </div>
                                <div class="my-0 mx-1 text-right">
                                    <div class="font-weight-bold text-right" style="font-size: 18px">
                                        {{ result.eventualScore() }}
                                    </div>
                                    <div class="grey--text caption my-0 text-right">
                                        <span v-if="result.latest_round === 'final_two'">F2</span>
                                        <span v-if="result.latest_round === 'final_one'">F1</span>
                                        <span v-if="result.latest_round === 'finals'">Final</span>
                                        <span v-if="result.latest_round === 'semi_final'">Q2</span>
                                        <span v-if="result.latest_round === 'prelims'">Qual</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <v-card-text class="pt-0">
                            <v-expansion-panels flat inset tile>
                                <v-expansion-panel>
                                    <v-expansion-panel-header>
                                        <div>
                                            <v-chip x-small class="mr-2">
                                                {{ result.videos.length }}
                                            </v-chip>
                                            <template v-if="result.videos.length === 1">Video</template>
                                            <template v-else>Videos</template>
                                        </div>
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content>
                                        <div class="d-flex flex-row justify-space-between mt-2">
                                            <div v-for="routine in result.routine_results" :key="routine.id">
                                                {{ routine.latest_round_short }} {{ routine.num }}
                                                <scorsync-video
                                                    :athlete="athlete"
                                                    :result-category="result.category"
                                                    :round="routine?.round"
                                                    :result="result"
                                                    :routine="routine"
                                                    size="24"
                                                    style="width: 24px; height: 24px"
                                                    class="mx-auto"
                                                />
                                            </div>
                                        </div>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-card-text>
                    </v-card>
                </template>
            </v-tab-item>

            <v-tab-item value="tab-qualification">
                <v-card>
                    <v-card-text>
                        <v-container v-if="user?.is_subscribed || user?.is_admin || $page.props.free_use">
                            <v-simple-table>
                                <template v-slot:default>
                                    <thead>
                                        <tr>
                                            <th class="text-center">Year</th>
                                            <th class="text-center">Trampoline</th>
                                            <th class="text-center">Double Mini</th>
                                            <th class="text-center">Tumbling</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(details, year) in qualification" :key="year">
                                            <td class="text-center">{{ year }}</td>
                                            <td class="text-center">
                                                <qualification-check :qualifier="details" event="TI" show-na="—" />
                                            </td>
                                            <td class="text-center">
                                                <qualification-check :qualifier="details" event="DM" show-na="—" />
                                            </td>
                                            <td class="text-center">
                                                <qualification-check :qualifier="details" event="TU" show-na="—" />
                                            </td>
                                        </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-container>

                        <v-container v-else>
                            <subscription-required />
                        </v-container>
                    </v-card-text>
                </v-card>
            </v-tab-item>

            <v-tab-item value="tab-mobility">
                <athlete-mobility :athlete-id="athleteId" />
            </v-tab-item>

            <v-tab-item value="tab-goals">
                <athlete-goals :athlete-id="athleteId" />
            </v-tab-item>

            <v-tab-item value="tab-stats">
                <v-card>
                    <v-card-text>
                        <athlete-trends
                            v-if="user?.is_subscribed || user?.is_admin || $page.props.free_use"
                            :athlete-id="athleteId"
                        />
                        <subscription-required v-else />
                    </v-card-text>
                </v-card>
            </v-tab-item>
        </v-tabs-items>
    </v-card>
</template>

<script>
import get from 'lodash/get.js';
import Athlete from '@/Models/Athlete.js';
import DatetimeDisplay from '@/Components/DatetimeDisplay.vue';
import { ordinal } from '@/helpers.js';
import AthleteTrends from '@/Components/AthleteTrends.vue';
import QualificationCheck from '@/Components/QualificationCheck.vue';
import RoutineVideo from '@/Components/RoutineVideo.vue';
import Video from '@/Models/Video.js';
import ScorsyncVideo from '@/Components/ScorsyncVideo.vue';
import User from '@/Models/User.js';
import SubscriptionRequired from '@/Components/SubscriptionRequired.vue';
import AthleteMobility from '@/Components/AthleteMobility.vue';
import AthleteGoals from '@/Components/AthleteGoals.vue';

export default {
    name: 'AthleteResults',

    components: {
        AthleteMobility,
        AthleteGoals,
        SubscriptionRequired,
        ScorsyncVideo,
        RoutineVideo,
        QualificationCheck,
        AthleteTrends,
        DatetimeDisplay,
    },

    props: {
        athleteId: {
            type: Number,
            required: true,
        },

        qualification: {
            type: Object,
            required: false,
            default: () => ({}),
        },

        tab: {
            type: String,
            required: false,
            default: 'tab-results',
        },
    },

    data: () => ({
        filterEvent: null,

        headers: null,

        events: [
            {
                text: 'TRA',
                value: 'TI',
            },
            {
                text: 'DMT',
                value: 'DM',
            },
            {
                text: 'TUM',
                value: 'TU',
            },
            {
                text: 'SYN',
                value: 'TS',
            },
        ],
    }),

    watch: {
        tab(newTab) {
            this.tab = newTab;
        },
    },

    computed: {
        user() {
            return User.find(this.$page.props.auth.user?.id);
        },

        athlete() {
            return Athlete.query()
                .with([
                    'results.meet',
                    'results.category',
                    'club',
                    'results.athlete2',
                    'results.club',
                    'results.club2',
                    'results.videos',
                    'results.trampoline_scores.video',
                    'results.double_mini_scores.video',
                    'results.tumbling_scores.video',
                    'results.synchro_scores.video',
                ])
                .with('results', (query) => {
                    if (this.filterEvent) {
                        query.where('event', this.filterEvent);
                    }

                    query.whereHas('meet').orderBy('created_at', 'desc');
                })
                .find(this.athleteId);
        },

        videos() {
            return Video.query()
                .with(['result.meet', 'routine'])
                .where('athlete_id', this.athleteId)
                .orderBy('created_at', 'desc')
                .get();
        },

        competedForDifferentClubs() {
            return _.uniqBy(this.athlete.results, 'club_id').length > 1;
        },
    },

    created() {
        this.headers = [
            {
                text: 'Year',
                sortable: false,
                value: 'meet.year',
            },
            {
                text: 'Meet',
                sortable: false,
                value: 'meet.title',
            },
            {
                text: 'Event',
                sortable: false,
                value: 'event',
            },
            {
                text: 'Level',
                sortable: false,
                value: 'level',
            },
        ];

        if (this.competedForDifferentClubs) {
            this.headers.push({
                text: 'Club',
                sortable: false,
                value: 'club.name',
            });
        }

        this.headers = [
            ...this.headers,
            {
                text: 'Score',
                sortable: false,
                value: 'total_score',
            },
            {
                text: 'Place',
                sortable: false,
                value: 'overall_placement',
            },
            {
                text: '',
                sortable: false,
                value: 'view',
            },
        ];
    },

    methods: {
        get,
        ordinal,

        href(result) {
            return `/result-categories/${result.category.id}`;
        },
    },
};
</script>

<style lang="scss" scoped>
.text-truncated {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 55vw;
}
</style>
